// src/components/dashboard/settings/Insurance.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

// Core Stable MUI Icons
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

const Insurance = () => {
  // Live Dark / Light Theme Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Exact dataset from your screenshot (with 13 full clinical entries)
  const [insuranceRows, setInsuranceRows] = useState([
    { id: 65, name: "MN MA (MN-ITS)", payerId: "MED789", program: "Medicaid", city: "" },
    { id: 64, name: "HENNEPIN HEALTH", payerId: "HEN456", program: "MCO", city: "" },
    { id: 63, name: "Blue Plus - PMAP", payerId: "BLU123", program: "PMAP", city: "" },
    { id: 59, name: "Dept of Human Services MN", payerId: "MNMCE", program: "Medicaid", city: "St. Paul" },
    { id: 42, name: "Blue Cross Blue Shield", payerId: "00726", program: "Medicaid", city: "Minneapolis" },
    { id: 32, name: "Health Partners", payerId: "hasnain miyan", program: "Medicaid", city: "Minneapolis" },
    { id: 18, name: "UCare Individual and Family Plans", payerId: "55414", program: "Medicaid", city: "Minneapolis" },
    { id: 16, name: "UHC - Optum Insurance", payerId: "55413", program: "Medicaid", city: "Eden Prairie" },
    { id: 15, name: "Medica Choice Care", payerId: "MED991", program: "Medicaid", city: "Minnetonka" },
    { id: 14, name: "PrimeWest Health", payerId: "PW4410", program: "MCO", city: "Alexandria" },
    { id: 12, name: "South Country Health Alliance", payerId: "SCHA01", program: "PMAP", city: "Owatonna" },
    { id: 10, name: "Itasca Medical Care (IMCare)", payerId: "IMC882", program: "Medicaid", city: "Grand Rapids" },
    { id: 8, name: "Aetna Better Health MN", payerId: "AET880", program: "MCO", city: "Bloomington" },
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [openModal, setOpenModal] = useState(false);

  // New Insurance Dialog State
  const [newEntry, setNewEntry] = useState({
    name: "",
    payerId: "",
    program: "Medicaid",
    city: "",
  });

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";

  // Handlers
  const handleDelete = (id) => {
    setInsuranceRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleCreate = () => {
    if (!newEntry.name.trim() || !newEntry.payerId.trim()) return;
    const nextId = Math.max(...insuranceRows.map((r) => r.id), 0) + 1;
    setInsuranceRows([{ id: nextId, ...newEntry }, ...insuranceRows]);
    setNewEntry({ name: "", payerId: "", program: "Medicaid", city: "" });
    setOpenModal(false);
  };

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Synchronized Clinical Program Badges
  const getProgramBadge = (program) => {
    switch (program) {
      case "Medicaid":
        return {
          bg: pastelTealBg,
          color: tealButton,
        };
      case "MCO":
        return {
          bg: isDark ? "rgba(168, 85, 247, 0.15)" : "#FAF5FF",
          color: isDark ? "#C084FC" : "#9333EA",
        };
      case "PMAP":
        return {
          bg: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7",
          color: isDark ? "#34D399" : "#15803D",
        };
      default:
        return {
          bg: isDark ? "rgba(255, 255, 255, 0.06)" : "#F1F5F9",
          color: secondaryTextColor,
        };
    }
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & ACTION BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ApartmentRoundedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                Insurance Management
              </Typography>
              <Chip
                label="Minnesota DHS"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Manage insurance payers, electronic submitter IDs, and plan affiliations
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          startIcon={<AddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            height: "40px",
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.82rem",
            borderRadius: "6px",
            px: 2.5,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#046337",
              boxShadow: "none",
            },
          }}
        >
          Add Insurance
        </Button>
      </Box>

      {/* 2. INSURANCE DATA TABLE CONTAINER */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "620px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "90px" }}>ID</TableCell>
                <TableCell>Insurance Name</TableCell>
                <TableCell>Payer ID</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>City</TableCell>
                <TableCell align="right" sx={{ width: "100px", pr: 3 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {insuranceRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const badge = getProgramBadge(row.program);

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 1.4,
                        },
                      }}
                    >
                      {/* ID Badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {row.id}
                        </Box>
                      </TableCell>

                      {/* Insurance Name */}
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.86rem" }}>
                        {row.name}
                      </TableCell>

                      {/* Payer ID */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            color: secondaryTextColor,
                          }}
                        >
                          {row.payerId}
                        </Typography>
                      </TableCell>

                      {/* Program Pill */}
                      <TableCell>
                        <Chip
                          label={row.program}
                          size="small"
                          sx={{
                            height: "22px",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            backgroundColor: badge.bg,
                            color: badge.color,
                          }}
                        />
                      </TableCell>

                      {/* City */}
                      <TableCell>
                        {row.city ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                            <LocationOnOutlinedIcon sx={{ fontSize: "0.95rem", color: secondaryTextColor }} />
                            <Typography sx={{ fontSize: "0.82rem", color: primaryTextColor, fontWeight: 500 }}>
                              {row.city}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.82rem", color: secondaryTextColor, opacity: 0.5 }}>
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Action (Delete) */}
                      <TableCell align="right" sx={{ pr: 2.5 }}>
                        <Tooltip title="Delete Insurance Payer">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                            sx={{
                              color: "#EF4444",
                              p: 0.6,
                              borderRadius: "6px",
                              "&:hover": {
                                backgroundColor: "rgba(239, 68, 68, 0.08)",
                              },
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={insuranceRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>

      {/* 3. ADD INSURANCE MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1.5px solid ${cardBorder}`,
            color: primaryTextColor,
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.15rem", color: primaryTextColor, pb: 1 }}>
          Add Insurance Company
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Insurance Company Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. UCare Individual and Family Plans"
              value={newEntry.name}
              onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Payer ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. MED789"
                value={newEntry.payerId}
                onChange={(e) => setNewEntry({ ...newEntry, payerId: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Program
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Medicaid, MCO, PMAP"
                value={newEntry.program}
                onChange={(e) => setNewEntry({ ...newEntry, program: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              City (Optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Minneapolis"
              value={newEntry.city}
              onChange={(e) => setNewEntry({ ...newEntry, city: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{
              color: secondaryTextColor,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: addGreen,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 2.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Save Payer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Insurance;